import {isFeatureEnabled} from 'feature-flags';

import {isNil} from 'ramda';

import {useGetParticipationQuery, useGetTenantIntegrationsAdvancedSearchQuery} from '@dms/api/core';
import {useGetVehicleQuery} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import {featureFlags} from '@dms/feature-flags';
import {usePermissions, useTenant} from '@dms/shared';

import {isVgpSupportedVehicle} from '../utils/isVgpSupportedVehicle';

const VGP_CERTIFICATE_INTEGRATION_CODE = 'VGP';

interface UseVgpCertificateAvailabilityProps {
  vehicleId: string;
}

interface UseVgpCertificateAvailabilityResult {
  isVgpCertificateAvailable: boolean;
  isLoading: boolean;
}

const DISABLED_RESULT: UseVgpCertificateAvailabilityResult = {
  isVgpCertificateAvailable: false,
  isLoading: false,
};

export function useVgpCertificateAvailability(
  props: UseVgpCertificateAvailabilityProps
): UseVgpCertificateAvailabilityResult {
  const isFeatureActive = isFeatureEnabled(featureFlags.SALES_VGP_CERTIFICATES);

  const {tenant} = useTenant();

  const {data: vehicle, isLoading: isVehicleLoading} = useGetVehicleQuery(
    {vehicleId: props.vehicleId},
    {skip: isNil(props.vehicleId) || !isFeatureActive}
  );

  const {data: vehicleParticipation, isLoading: isParticipationLoading} = useGetParticipationQuery(
    {
      resourceId: EntityResourceIds.vehicle,
      recordId: props.vehicleId,
    },
    {skip: isNil(props.vehicleId) || !isFeatureActive}
  );

  const [canGenerateVgpCertificate] = usePermissions({
    permissionKeys: ['generateVgpCertificate'],
    scopes: {
      generateVgpCertificate: {
        participation: vehicleParticipation,
        branchId: vehicle?.branchId,
      },
    },
  });

  const tenantId = tenant?.id;
  const isVehicleSupported = isVgpSupportedVehicle(vehicle?.make, vehicle?.type);

  const {data: integrationData, isLoading: isSearchingIntegrations} =
    useGetTenantIntegrationsAdvancedSearchQuery(
      {
        tenantId: tenantId!,
        branchId: vehicle?.branchId ? [vehicle.branchId] : undefined,
        integrationCode: [VGP_CERTIFICATE_INTEGRATION_CODE],
      },
      {skip: isNil(tenantId) || !isVehicleSupported || !isFeatureActive}
    );

  if (!isFeatureActive) {
    return DISABLED_RESULT;
  }

  const hasVgpIntegration = (integrationData?.length ?? 0) > 0;

  const isLoading = isVehicleLoading || isParticipationLoading || isSearchingIntegrations;

  const isVgpCertificateAvailable =
    canGenerateVgpCertificate && isVehicleSupported && hasVgpIntegration;

  return {
    isVgpCertificateAvailable,
    isLoading,
  };
}
